import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  type PlanChangeRequestSummary,
  useRejectPlanChangeRequest,
} from "@/hooks/usePlanChangeRequests";

interface Props {
  request: PlanChangeRequestSummary | null;
  onClose: () => void;
  onRejected: () => void;
}

export const RejectPlanChangeDialog = ({
  request,
  onClose,
  onRejected,
}: Props) => {
  const [rejectionReason, setRejectionReason] = useState("");
  const [note, setNote] = useState("");
  const reject = useRejectPlanChangeRequest();

  useEffect(() => {
    if (!request) {
      setRejectionReason("");
      setNote("");
    }
  }, [request]);

  const handleReject = async () => {
    if (!request) return;
    if (rejectionReason.trim().length < 3) {
      toast.error("Please provide a rejection reason.");
      return;
    }
    try {
      await reject.mutateAsync({
        id: request.id,
        rejectionReason: rejectionReason.trim(),
        note: note.trim() || undefined,
      });
      toast.success("Plan change request rejected.");
      onRejected();
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  return (
    <Dialog open={!!request} onOpenChange={(open) => (open ? null : onClose())}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Reject plan change</DialogTitle>
          <DialogDescription>
            The owner will see this reason in their request history.
          </DialogDescription>
        </DialogHeader>
        {request ? (
          <div className="space-y-3">
            <div className="text-sm">
              {request.owner.name ?? "—"} ·{" "}
              <span className="text-muted-foreground">
                {request.currentPlan?.name ?? "—"} → {request.targetPlan.name}
              </span>
            </div>
            <div>
              <Label htmlFor="reason">Reason</Label>
              <Textarea
                id="reason"
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="e.g. Duplicate request — please contact support."
                rows={3}
              />
            </div>
            <div>
              <Label htmlFor="note">Internal note (optional)</Label>
              <Textarea
                id="note"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={2}
              />
            </div>
          </div>
        ) : null}
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={reject.isPending}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleReject}
            disabled={reject.isPending}
          >
            Reject request
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
